import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { useI18n } from "../../i18n/LanguageContext";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";
import { Play, StopCircle, RefreshCw, Sparkles, AlertTriangle, Zap } from "lucide-react";

export default function SuperAdminContent() {
  const { t } = useI18n();
  const [jobs, setJobs] = useState([]);
  const [zombies, setZombies] = useState([]);
  const [busy, setBusy] = useState(false);
  const [limit, setLimit] = useState(3);
  const [mode, setMode] = useState("full");

  const active = jobs.find((j) => j.status === "running" || j.status === "queued");
  const zombieIds = new Set(zombies.map((z) => z.job_id));

  const load = async () => {
    try {
      const [rows, z] = await Promise.all([
        api.saSeedJobs({ limit: 20 }),
        api.saSeedZombies(15).catch(() => ({ zombies: [] })),
      ]);
      setJobs(rows);
      setZombies(z?.zombies || []);
    } catch (e) { toast.error(e.message); }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, []);

  const start = async () => {
    if (!window.confirm(
      mode === "dry_run"
        ? "Start dry-run seed (no writes)?"
        : `Start AI content seed (${limit || 45} courses)? This will overwrite matching course content and remove Phase 2 mini-seed orphans.`
    )) return;
    setBusy(true);
    try {
      const body = { mode };
      if (limit && Number(limit) > 0 && Number(limit) < 45) body.limit_courses = Number(limit);
      const r = await api.saSeedStart(body);
      toast.success(`Job ${r.job_id} queued`);
      load();
    } catch (e) { toast.error(e.reason || e.message); }
    finally { setBusy(false); }
  };

  const cancel = async (jobId) => {
    if (!window.confirm("Cancel this job? In-flight courses may finish; further courses will be skipped.")) return;
    try { await api.saSeedCancel(jobId); toast.success("Cancel requested"); load(); }
    catch (e) { toast.error(e.message); }
  };

  const forceReset = async (jobId) => {
    if (!window.confirm(
      `Force-reset job ${jobId}?\n\n` +
      "Use this only when the async worker is dead (pod recycle / silently hung LLM call). " +
      "This marks the job as cancelled and appends an audit trail entry, releasing the 'one-job-at-a-time' lock."
    )) return;
    try {
      const r = await api.saSeedForceReset(jobId);
      toast.success(r.already_terminal ? "Job was already terminal — no-op." : `Force-reset OK: ${jobId}`);
      load();
    } catch (e) { toast.error(e.reason || e.message); }
  };

  return (
    <div className="space-y-6" data-testid="sa-content-page">
      <header>
        <div className="text-xs font-bold uppercase tracking-widest text-[#F72585]">{t("superadmin.title")}</div>
        <h1 className="text-3xl md:text-4xl font-black tracking-tight">{t("superadmin.content")}</h1>
        <div className="text-sm text-black/60 mt-1">AI-authored bilingual 45-course seed. Idempotent.</div>
      </header>

      {zombies.length > 0 && (
        <div className="rounded-2xl border-2 border-[#D90429]/40 bg-[#FEE2E2] p-4 space-y-2"
             data-testid="sa-seed-zombie-banner">
          <div className="flex items-center gap-2 font-black text-[#991B1B]">
            <AlertTriangle className="h-5 w-5" /> {zombies.length} zombie seed job{zombies.length === 1 ? "" : "s"} detected
          </div>
          <div className="text-xs text-[#7A1220] font-semibold">
            These jobs are marked "running" but haven't updated for 15+ min — likely a dead async worker.
            Force-reset to release the lock and start a fresh seed.
          </div>
          <div className="flex flex-wrap gap-2 pt-1">
            {zombies.map((z) => (
              <Button
                key={z.job_id}
                onClick={() => forceReset(z.job_id)}
                className="rounded-full bg-[#D90429] hover:bg-[#B00020] text-white font-bold px-4 py-2 text-xs shadow-[0_3px_0_#7A1220]"
                data-testid={`sa-seed-force-reset-${z.job_id}`}
              >
                <Zap className="h-3.5 w-3.5 me-1" /> Force-reset {z.job_id}
              </Button>
            ))}
          </div>
        </div>
      )}

      <div className="rounded-3xl bg-white border border-black/5 shadow-[0_2px_0_rgba(0,0,0,0.04)] p-6 space-y-4"
           data-testid="sa-seed-control">
        <div className="flex items-center gap-3">
          <Sparkles className="h-5 w-5 text-[#F72585]" />
          <h2 className="font-black text-lg">Run seed</h2>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <label className="text-xs font-bold uppercase text-black/60">
            Mode
            <select value={mode} onChange={(e) => setMode(e.target.value)}
                    className="mt-1 block h-10 rounded-md border border-black/10 px-3 font-semibold bg-white text-sm"
                    data-testid="sa-seed-mode">
              <option value="full">Full (writes)</option>
              <option value="dry_run">Dry-run</option>
            </select>
          </label>
          <label className="text-xs font-bold uppercase text-black/60">
            Limit courses (0 = all 45)
            <input type="number" min={0} max={45} value={limit}
                   onChange={(e) => setLimit(e.target.value)}
                   className="mt-1 block h-10 w-32 rounded-md border border-black/10 px-3 font-semibold bg-white text-sm"
                   data-testid="sa-seed-limit" />
          </label>
          <Button
            onClick={start}
            disabled={busy || !!active}
            className="rounded-full bg-[#F72585] hover:bg-[#F72585]/90 text-white font-bold px-6 shadow-[0_3px_0_#B00060] disabled:opacity-40"
            data-testid="sa-seed-start-btn"
          >
            <Play className="h-4 w-4 me-1" /> Start
          </Button>
          {active && (
            <Button
              onClick={() => cancel(active.job_id)}
              className="rounded-full bg-[#D90429]/15 text-[#D90429] font-bold px-4"
              data-testid="sa-seed-cancel-btn"
            >
              <StopCircle className="h-4 w-4 me-1" /> Cancel current
            </Button>
          )}
          <Button onClick={load} variant="outline" className="rounded-full font-bold ms-auto"
                  data-testid="sa-seed-refresh-btn">
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>

        {active && (
          <div className="rounded-2xl border-2 border-[#F72585]/30 bg-[#F72585]/5 p-4"
               data-testid="sa-seed-active">
            <div className="text-xs font-black uppercase text-[#F72585]">Job running</div>
            <div className="mt-1 text-sm font-bold">{active.job_id}</div>
            <div className="mt-2 flex flex-wrap gap-3 text-xs text-black/70">
              <span>Generated: <b>{active.courses_generated || 0}</b></span>
              <span>Failed: <b>{active.courses_failed || 0}</b></span>
              <span>Skipped: <b>{active.courses_skipped || 0}</b></span>
              <span>Total: <b>{active.total_courses || "…"}</b></span>
            </div>
            {active.current_course_title_en && (
              <div className="mt-2 text-sm text-black/80">Currently: <b>{active.current_course_title_en}</b></div>
            )}
            {active.errors?.length > 0 && (
              <div className="mt-3 text-xs bg-white rounded-lg p-2 max-h-40 overflow-y-auto">
                <div className="font-black text-[#D90429] mb-1 flex items-center gap-1">
                  <AlertTriangle className="h-3 w-3" /> {active.errors.length} error(s)
                </div>
                {active.errors.slice(-5).map((e, i) => (
                  <div key={i} className="text-black/60 py-0.5">
                    <b>{e.course_id?.slice(-8) || "-"}</b>: {e.message}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="rounded-2xl border border-black/5 bg-white overflow-x-auto" data-testid="sa-seed-jobs-table">
        <table className="w-full text-sm">
          <thead className="bg-black/5">
            <tr>
              <Th>job_id</Th><Th>mode</Th><Th>status</Th><Th>gen / fail / skip / total</Th>
              <Th>created</Th><Th>duration</Th><Th>errors</Th><Th>actions</Th>
            </tr>
          </thead>
          <tbody>
            {jobs.length === 0 && (
              <tr><td colSpan={8} className="p-6 text-center text-black/50">No jobs yet.</td></tr>
            )}
            {jobs.map((j) => (
              <tr key={j.job_id} className={`border-t border-black/5 ${zombieIds.has(j.job_id) ? "bg-[#FEE2E2]/40" : ""}`}
                  data-testid={`sa-seed-job-${j.job_id}`}>
                <Td className="font-mono text-xs">
                  {j.job_id}
                  {zombieIds.has(j.job_id) && (
                    <span className="ms-2 inline-flex items-center gap-1 rounded-full bg-[#D90429]/15 text-[#D90429] text-[10px] font-black px-2 py-0.5 uppercase tracking-wider" data-testid={`sa-seed-zombie-pill-${j.job_id}`}>
                      <AlertTriangle className="h-3 w-3" /> Zombie
                    </span>
                  )}
                </Td>
                <Td>
                  <Badge className="text-[10px]">{j.mode || (j.dry_run ? "dry_run" : "full")}</Badge>
                </Td>
                <Td>
                  <Badge className={`text-[10px] ${
                    j.status === "completed" ? "bg-green-500/15 text-green-700" :
                    j.status === "running" ? "bg-[#FFB703]/20 text-[#7A4B00] animate-pulse" :
                    j.status === "cancelled" ? "bg-black/10 text-black/60" :
                    j.status === "completed_with_errors" ? "bg-[#FFB703]/30 text-[#7A4B00]" :
                    j.status === "failed" ? "bg-[#D90429]/20 text-[#D90429]" :
                    "bg-black/5 text-black/60"
                  }`}>{j.status}</Badge>
                </Td>
                <Td className="text-xs font-mono">
                  {j.courses_generated || 0} / {j.courses_failed || 0} / {j.courses_skipped || 0} / {j.total_courses || "-"}
                </Td>
                <Td className="text-xs text-black/60">{j.created_at?.slice(0, 19).replace("T", " ")}</Td>
                <Td className="text-xs text-black/60">
                  {j.started_at && j.finished_at
                    ? `${Math.round((new Date(j.finished_at) - new Date(j.started_at)) / 1000)}s`
                    : (j.started_at ? "…" : "-")}
                </Td>
                <Td className="text-xs">{(j.errors || []).length}</Td>
                <Td>
                  {zombieIds.has(j.job_id) && (
                    <button
                      onClick={() => forceReset(j.job_id)}
                      className="rounded-full bg-[#D90429] hover:bg-[#B00020] text-white font-bold px-3 py-1 text-[11px] shadow-[0_2px_0_#7A1220]"
                      data-testid={`sa-seed-force-reset-row-${j.job_id}`}
                    >
                      <Zap className="inline h-3 w-3 me-1" /> Force-reset
                    </button>
                  )}
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Th({ children }) { return <th className="text-start px-3 py-2.5 text-[11px] font-black uppercase tracking-wider text-black/60">{children}</th>; }
function Td({ children, className = "" }) { return <td className={`px-3 py-2.5 ${className}`}>{children}</td>; }
